/* eslint-disable camelcase */
import {
  PaBlockableAction,
  PaCdvAckStatus,
  PaCdvDirection,
  PaVatBreakdownLine,
  PaVatSplit,
  TreasuryFullDocuments,
} from "../store/ducks/treasury.ducks"

export const PA_CDV_FILTER_ALL = "all"
export const PA_CDV_FILTER_PA_ONLY = "pa_only"
export const PA_CDV_FILTER_NON_PA = "non_pa"

export const PA_REJECT_REASON_CODES = [
  "ADR_ERR",
  "CALCUL_ERR",
  "CMD_ERR",
  "CONTRAT_TERM",
  "DEST_ERR",
  "DOUBLE_FACT",
  "DOUBLON",
  "EMMET_INC",
  "MONTANTTOTAL_ERR",
  "NON_CONFORME",
  "REF_CT_ABSENT",
  "TRANSAC_INC",
  "TX_TVA_ERR",
] as const

// XP Z12-014 : statuts d'alerte affiches en orange dans l'historique.
const ALERT_CODES = ["207", "208", "210", "213", "220", "221", "501"]
const TERMINAL_CODES = ["213", "220", "221", "501"]

export const ROLE_CODES = ["BY", "SE", "AB", "SR", "WK", "PE"] as const

// Alerte a afficher sur la facture pour un statut recu (refus, litige, rejet, paiement annonce).
export const paCdvAlertKey = (fullDocument: TreasuryFullDocuments) => {
  if (
    !fullDocument.pa_invoice ||
    fullDocument.last_cdv_direction !== "received"
  )
    return null
  const code = fullDocument.last_cdv_code || ""
  if (code === "211" && fullDocument.buy_or_sell === "sell")
    return "treasury.pa_cdv.alert.payment_announced"
  if (ALERT_CODES.includes(code)) return `treasury.pa_cdv.alert.${code}`
  return null
}

export type CdvTone = "success" | "pending" | "error" | "alert"

export const toCents = (amount: string | number | null | undefined) => {
  const value = Number(amount)
  if (amount === "" || amount === null || amount === undefined || isNaN(value))
    return 0
  return Math.round(value * 100)
}

const fromCents = (cents: number) => (cents / 100).toFixed(2)

// Encaissements partiels successifs : le formulaire part de ce qui reste a declarer.
export const remainingToCollect = (fullDocument: TreasuryFullDocuments) =>
  fromCents(
    Math.max(
      toCents(fullDocument.amount_tax_included) -
        toCents(fullDocument.amount_collected),
      0
    )
  )

// Apres un 212, la PA accepte un nouveau 212 (et bloque le retour a recevoir) : on propose un nouvel
// encaissement tant qu'il reste a encaisser.
export const canCollectAgain = (fullDocument: TreasuryFullDocuments) =>
  fullDocument.pa_invoice &&
  isActionBlocked(fullDocument, "mark_as_to_receive") &&
  !isActionBlocked(fullDocument, "mark_as_received") &&
  toCents(remainingToCollect(fullDocument)) > 0

export const prorataSplits = (
  breakdown: PaVatBreakdownLine[],
  amount: string
): PaVatSplit[] => {
  const amountCents = toCents(amount)
  if (breakdown.length === 0)
    return [{ vat_rate: "0", amount: fromCents(amountCents) }]

  const totalCents = breakdown.reduce(
    (sum, line) => sum + toCents(line.amount_tax_included),
    0
  )
  let remaining = amountCents
  return breakdown.map((line, index) => {
    const share =
      index === breakdown.length - 1 || totalCents === 0
        ? remaining
        : Math.round(
            (amountCents * toCents(line.amount_tax_included)) / totalCents
          )
    remaining -= share
    return { vat_rate: line.vat_rate, amount: fromCents(share) }
  })
}

export const splitsMatchAmount = (splits: PaVatSplit[], amount: string) => {
  const amountCents = toCents(amount)
  if (amountCents <= 0) return false
  if (splits.some((split) => toCents(split.amount) <= 0)) return false
  return (
    splits.reduce((sum, split) => sum + toCents(split.amount), 0) ===
    amountCents
  )
}

export const isActionBlocked = (
  fullDocument: TreasuryFullDocuments,
  action: PaBlockableAction
) =>
  Boolean(fullDocument.pa_invoice) &&
  (fullDocument.cdv_blocked_actions || []).includes(action)

// Libelle i18n du blocage, a afficher sur le bouton desactive : null si l'action est permise.
export const blockedReasonKey = (
  fullDocument: TreasuryFullDocuments,
  action: PaBlockableAction
) => {
  if (!isActionBlocked(fullDocument, action)) return null
  const code = fullDocument.last_cdv_code || ""
  if (TERMINAL_CODES.includes(code)) return "treasury.pa_cdv.blocked.terminal"
  if (code === "210") return "treasury.pa_cdv.blocked.refused"
  if (code === "212") return "treasury.pa_cdv.blocked.collected"
  return "treasury.pa_cdv.blocked.other"
}

export const isEveryDocumentBlocked = (
  fullDocuments: TreasuryFullDocuments[],
  action: PaBlockableAction
) =>
  fullDocuments.length > 0 &&
  fullDocuments.every((fullDocument) => isActionBlocked(fullDocument, action))

export const paInvoicesOf = (
  fullDocuments: TreasuryFullDocuments[],
  action: PaBlockableAction
) =>
  fullDocuments.filter(
    (fullDocument) =>
      fullDocument.pa_invoice && !isActionBlocked(fullDocument, action)
  )

export const filterByLastCdv = (
  fullDocuments: TreasuryFullDocuments[],
  filter: string
) => {
  if (filter === PA_CDV_FILTER_ALL) return fullDocuments
  if (filter === PA_CDV_FILTER_PA_ONLY)
    return fullDocuments.filter((fullDocument) => fullDocument.pa_invoice)
  if (filter === PA_CDV_FILTER_NON_PA)
    return fullDocuments.filter((fullDocument) => !fullDocument.pa_invoice)
  return fullDocuments.filter(
    (fullDocument) =>
      fullDocument.pa_invoice && fullDocument.last_cdv_code === filter
  )
}

export const lastCdvStatusesOf = (fullDocuments: TreasuryFullDocuments[]) => {
  const labels = new Map<string, string>()
  fullDocuments.forEach((fullDocument) => {
    if (!fullDocument.pa_invoice || !fullDocument.last_cdv_code) return
    if (!labels.has(fullDocument.last_cdv_code))
      labels.set(fullDocument.last_cdv_code, fullDocument.last_cdv_label || "")
  })
  return Array.from(labels, ([code, label]) => ({ code, label })).sort((a, b) =>
    a.code.localeCompare(b.code)
  )
}

export const cdvTone = (
  code: string,
  direction: PaCdvDirection,
  ack: PaCdvAckStatus | null
): CdvTone => {
  if (direction === "emitted" && ack === "Error") return "error"
  if (direction === "emitted" && ack !== "Ok") return "pending"
  if (ALERT_CODES.includes(code)) return "alert"
  return "success"
}

export const newIdempotencyKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

// Une cle par ouverture de modale : un nouvel essai apres une erreur reutilise la meme, et le
// serveur ne refait pas une action deja traitee dont la reponse s'est perdue.
export const withIdempotencyKey = <T extends { idempotency_key?: string }>(
  run: (options: T) => unknown
) => {
  const key = newIdempotencyKey()
  return (options: T) => run({ ...options, idempotency_key: key })
}

const RECEIVED_ALERT_CODES = ["207", "208", "210", "211"]
const TERMINAL_ALERT_CODES = ["213", "220", "221", "501"]

export interface PaCdvAlert {
  code: string
  reasonCode: string | null
  note: string | null
  amount: string | null
  valueDate: string | null
}

// Alerte a afficher sur la ligne : refus, litige ou paiement annonce par le client, ou statut terminal.
export const paCdvAlertOf = (
  fullDocument: TreasuryFullDocuments
): PaCdvAlert | null => {
  const code = fullDocument.last_cdv_code
  if (!fullDocument.pa_invoice || !code) return null
  const receivedOnSale =
    fullDocument.last_cdv_direction === "received" &&
    fullDocument.buy_or_sell === "sell" &&
    RECEIVED_ALERT_CODES.includes(code)
  if (!receivedOnSale && !TERMINAL_ALERT_CODES.includes(code)) return null

  return {
    code,
    reasonCode: fullDocument.last_cdv_reason_code,
    note: fullDocument.last_cdv_note,
    amount: fullDocument.last_cdv_amount,
    valueDate: fullDocument.last_cdv_value_date,
  }
}
